import { useEffect, useMemo, useState } from 'react'
import Header from '../components/layout/Header'
import SearchBar from '../components/public-pools/SearchBar'
import PoolsTable from '../components/public-pools/PoolsTable'
import { PUBLIC_POOLS_SEO } from '../config/seo'
import { usePageCss } from '../hooks/usePageCss'
import { sortPools } from '../data/publicPools'
import { usePageMeta } from '../hooks/usePageMeta'
import {
  fetchPublicPools,
  isPublicPoolAccount,
  normalizePublicPool,
} from '../lib/lighterApi'
import './public-pools-mobile.css'

const USER_TABLE_MAX_HEIGHT = 872

function matchesQuery(pool, query) {
  if (!query) return true
  const haystack = [
    pool.name,
    pool.operatorLabel,
    pool.address,
    pool.isProtocol ? 'lighter' : 'community',
  ]
    .join(' ')
    .toLowerCase()
  return haystack.includes(query)
}

export default function PublicPools() {
  usePageMeta(PUBLIC_POOLS_SEO)
  usePageCss('public-pools-page-css', '/css/lighter_public-pools.css')

  const [protocolPools, setProtocolPools] = useState([])
  const [userPools, setUserPools] = useState([])
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState('tvl')
  const [sortDirection, setSortDirection] = useState('desc')

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const raw = await fetchPublicPools({
          filter: 'all',
          limit: 100,
          maxPages: 20,
        })
        if (cancelled) return

        const pools = raw
          .filter(isPublicPoolAccount)
          .map((row) => normalizePublicPool(row))

        const protocol = pools.filter(
          (pool) => pool.isProtocol && !pool.name.includes('(XLP)')
        )
        const users = pools.filter((pool) => !pool.isProtocol)

        setProtocolPools(sortPools(protocol, { key: 'tvl', direction: 'desc' }))
        setUserPools(users)
      } catch (error) {
        console.error('Failed to load public pools', error)
      }
    }

    load()
    const interval = setInterval(load, 30_000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  const filteredUserPools = useMemo(() => {
    const query = search.trim().toLowerCase()
    const filtered = userPools.filter((pool) => matchesQuery(pool, query))
    return sortPools(filtered, { key: sortKey, direction: sortDirection })
  }, [userPools, search, sortKey, sortDirection])

  const onSort = (key) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'))
      return
    }
    setSortKey(key)
    setSortDirection('desc')
  }

  return (
    <div className="public-pools-page flex h-dvh w-full flex-col overflow-hidden bg-bg text-ink">
      <Header />
      <div
        data-testid="sub-page-scroll-region"
        className="min-h-0 flex-1 overflow-y-auto"
        data-fade="y"
        style={{ '--fade-t': '0px', '--fade-b': '24px' }}
      >
        <div className="mx-auto w-full max-w-[1240px] px-8 py-10 max-mobile:px-4 max-mobile:py-6">
          <section
            className="section-in max-mobile:mb-6 mb-8"
            style={{ '--sec-d': '0s' }}
          >
            <div className="flex items-end justify-between gap-6 max-mobile:flex-col max-mobile:items-start max-mobile:gap-4">
              <div className="min-w-0">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                  <h1 className="m-0 text-hero leading-[1.02] font-medium tracking-display [text-box-edge:cap_alphabetic] [text-box-trim:trim-start]">
                    Public Pools
                  </h1>
                </div>
              </div>
              <div className="shrink-0 max-mobile:w-full" />
            </div>
            <div className="mt-4 max-w-[60%] text-xl/2xl text-pretty text-dim max-mobile:max-w-none">
              Deposit into protocol or community-managed liquidity pools and earn
              a share of their performance.
            </div>
          </section>

          <section className="section-in mb-3" style={{ '--sec-d': '0.06s' }}>
            <div className="overflow-hidden rounded-panel border-[0.5px] border-line bg-fill">
              <div className="card-header border-b-[0.5px] border-line flex min-h-(--control-h-xl) items-center justify-between gap-3 p-2 max-mobile:flex-col max-mobile:items-stretch">
                <h2 className="m-0 text-lg font-medium text-ink">
                  Protocol Public Pools
                </h2>
              </div>
              <PoolsTable
                testId="protocol-public-pools-table"
                pools={protocolPools}
              />
            </div>
          </section>

          <section className="section-in" style={{ '--sec-d': '0.12s' }}>
            <div className="overflow-hidden rounded-panel border-[0.5px] border-line bg-fill">
              <div className="card-header border-b-[0.5px] border-line flex min-h-(--control-h-xl) items-center justify-between gap-3 p-2 max-mobile:flex-col max-mobile:items-stretch">
                <h2 className="m-0 text-lg font-medium text-ink">
                  User Public Pools
                </h2>
                <SearchBar value={search} onChange={setSearch} />
              </div>
              <PoolsTable
                testId="user-public-pools-table"
                pools={filteredUserPools}
                sortable
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={onSort}
                maxHeight={USER_TABLE_MAX_HEIGHT}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
